'use client';

import { Slot } from '@radix-ui/react-slot';
import React, { useRef } from 'react';
import { useClickAway } from '../../hooks/useClickAway/useClickAway';

export interface ClickAwayListenerProps {
  /**
   * Render as child component.
   * @default false
   */
  asChild?: boolean;
  /**
   * The children to render.
   */
  children: React.ReactNode;
  /**
   * The class name to add to the component.
   */
  className?: string;
  /**
   * Callback fired when a "click away" event is detected.
   */
  onClickAway: (event: MouseEvent | TouchEvent) => void;
  /**
   * The strategy to use for listening to click events.
   * @default 'start'
   */
  strategy?: 'start' | 'click';
}

/**
 * A component that listens for clicks outside of its children and calls a callback when it happens.
 *
 * @params onClickAway - Callback fired when a "click away" event is detected. This needs to be dependency-stable, so don't forget to use `useCallback`.
 */
const ClickAwayListener = ({
  asChild = false,
  children,
  className,
  onClickAway,
  strategy,
}: ClickAwayListenerProps) => {
  const node = useRef<HTMLDivElement | null>(null);

  const Comp = asChild ? Slot : 'div';

  useClickAway(node, { onClickAway, strategy });

  return (
    <Comp className={className} ref={node}>
      {children}
    </Comp>
  );
};

ClickAwayListener.displayName = 'ClickAwayListener';

export { ClickAwayListener };
